import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { PRODUCT_TYPES } from "../api/products/productBaseConstants";
import { productSelectors } from "../components/products/_core/productSelectors";

const BRAND = "GODZILLA";

const HOME = {
  title: "Заказать суши с доставкой в Минске - GODZILLA",
  description:
    "Суши в Минске с доставкой на дом от GODZILLA. Закажите суши с доставкой - большой выбор роллов, сетов и других блюд с быстрой доставкой. Суши за 60 минут или самое дорогое блюдо из заказа за 1 копейку.",
};

// Пререндер карточек не делаем: это CRA без сервера, который отдаёт свой HTML
// на каждый URL. Каталог приходит с API уже в браузере, поэтому title товара
// ставится здесь, а в sitemap карточки не входят — иначе Google получит оболочку
// главной. Статический title главной остаётся в HTML до выполнения JS.
const PAGE_META = [
  { path: "/", ...HOME },
  {
    path: "/cart",
    title: "Корзина - GODZILLA",
    description:
      "Корзина GODZILLA: проверьте состав заказа, измените количество блюд и перейдите к оформлению доставки или самовывоза.",
  },
  {
    path: "/payments",
    title: "Способы оплаты - GODZILLA",
    description:
      "Способы оплаты заказов в GODZILLA. Узнайте, какие варианты оплаты доступны и как оплатить заказ.",
  },
  {
    path: "/rules",
    title: "Публичная оферта о продаже товаров - GODZILLA",
    description:
      "Публичная оферта GODZILLA: условия продажи товаров, оформления заказа и основные положения договора между покупателем и компанией.",
  },
  {
    path: "/information",
    title: "Вопросы и ответы - GODZILLA",
    description:
      "Ответы на частые вопросы о заказе в GODZILLA: оформление заказа, доставка, оплата и другая полезная информация для покупателей.",
  },
  {
    path: "/delivery-zone",
    title: "Зона доставки - GODZILLA",
    description:
      "Зона доставки GODZILLA: узнайте, куда мы доставляем заказы, условия и стоимость, а также доступные зоны доставки.",
  },
  {
    path: "/pickup",
    title: "Пункты самовывоза - GODZILLA",
    description:
      "Адреса пунктов самовывоза GODZILLA на карте Минска. Найдите удобную для вас точку, чтобы забрать заказ самостоятельно в удобное время.",
  },
  {
    path: "/privacy-policy",
    title: "Политика обработки персональных данный - GODZILLA",
    description:
      "Политика обработки персональных данных на сайте GODZILLA. Условия сбора, обработки, использования, хранения и защиты персональных данных пользователей.",
  },
  {
    path: "/shipping-settings",
    title: "Укажите ваш адрес - GODZILLA",
    description:
      "Укажите адрес доставки в GODZILLA, чтобы узнать возможность и стоимость доставки, доступные зоны и условия получения заказа.",
  },
  {
    path: "/online/success",
    title: "Спасибо, ваш заказ принят - GODZILLA",
    description:
      "Спасибо за заказ в GODZILLA! Ваш заказ успешно принят и передан в обработку. Информация о заказе доступна на странице подтверждения.",
  },
  {
    path: "/audio-and-video-policy",
    title: "Политика видео и аудио наблюдения | GODZILLA",
    description:
      "Политика видео- и аудионаблюдения GODZILLA: информация о ведении видеонаблюдения, аудиозаписи, целях обработки и защите данных посетителей.",
  },
  {
    path: "/vacancies",
    title: "Вакансии | GODZILLA",
    description:
      "Сеть GODZILLA приглашает на работу в Минске. Смотрите открытые вакансии и оставляйте отклик.",
  },
];

const PRODUCT_ROUTES = [
  { prefix: "/rolls-detail/", selector: PRODUCT_TYPES.roll.id },
  { prefix: "/rollset-detail/", selector: PRODUCT_TYPES.rollset.id },
  { prefix: "/soup-detail/", selector: PRODUCT_TYPES.soup.id },
  { prefix: "/garnish-detail/", selector: PRODUCT_TYPES.garnish.subtypes.garnish.id },
  { prefix: "/sauce-detail/", selector: PRODUCT_TYPES.garnish.subtypes.sauce.id },
];

const setMetaContent = (selector, content) => {
  const node = document.head.querySelector(selector);
  if (node && content) {
    node.setAttribute("content", content);
  }
};

const applyMeta = ({ title, description }) => {
  document.title = title;
  setMetaContent('meta[name="description"]', description);
  setMetaContent('meta[property="og:title"]', title);
  setMetaContent('meta[property="og:description"]', description);
};

const findInMap = (map, id) => (map && map.get ? map.get(id) : null);

export function DocumentMeta() {
  const { pathname } = useLocation();
  const rolls = useSelector(productSelectors[PRODUCT_TYPES.roll.id]);
  const rollsets = useSelector(productSelectors[PRODUCT_TYPES.rollset.id]);
  const soups = useSelector(productSelectors[PRODUCT_TYPES.soup.id]);
  const garnishes = useSelector(productSelectors[PRODUCT_TYPES.garnish.subtypes.garnish.id]);
  const sauces = useSelector(productSelectors[PRODUCT_TYPES.garnish.subtypes.sauce.id]);
  const pokesStandard = useSelector(productSelectors[PRODUCT_TYPES.poke.subtypes.standard.id]);
  const pokesLight = useSelector(productSelectors[PRODUCT_TYPES.poke.subtypes.light.id]);
  const pokesSashimi = useSelector(productSelectors[PRODUCT_TYPES.poke.subtypes.sashimi.id]);
  const cakes = useSelector(productSelectors[PRODUCT_TYPES.dessert.subtypes.cake.id]);
  const cookies = useSelector(productSelectors[PRODUCT_TYPES.dessert.subtypes.cookie.id]);
  const pies = useSelector(productSelectors[PRODUCT_TYPES.dessert.subtypes.pie.id]);
  const dessertRolls = useSelector(productSelectors[PRODUCT_TYPES.dessert.subtypes.dessertRoll.id]);

  useEffect(() => {
    const productRoute = PRODUCT_ROUTES.find((route) => pathname.startsWith(route.prefix));
    if (productRoute || pathname.startsWith("/poke-detail/") || pathname.startsWith("/dessert-detail/")) {
      const id = pathname.split("/")[2];
      const catalogs = {
        [PRODUCT_TYPES.roll.id]: rolls,
        [PRODUCT_TYPES.rollset.id]: rollsets,
        [PRODUCT_TYPES.soup.id]: soups,
        [PRODUCT_TYPES.garnish.subtypes.garnish.id]: garnishes,
        [PRODUCT_TYPES.garnish.subtypes.sauce.id]: sauces,
      };
      let product = productRoute ? findInMap(catalogs[productRoute.selector], id) : null;
      if (pathname.startsWith("/poke-detail/")) {
        product = findInMap(pokesStandard, id) || findInMap(pokesLight, id) || findInMap(pokesSashimi, id);
      }
      if (pathname.startsWith("/dessert-detail/")) {
        product = findInMap(cakes, id) || findInMap(cookies, id) || findInMap(pies, id) || findInMap(dessertRolls, id);
      }
      if (product && product.title) {
        applyMeta({
          title: `${String(product.title).toLowerCase()} - ${BRAND}`,
          description: product.announce || HOME.description,
        });
        return;
      }
    }

    const page = PAGE_META.find((item) => item.path === pathname) || HOME;
    applyMeta(page);
  }, [
    pathname,
    rolls,
    rollsets,
    soups,
    garnishes,
    sauces,
    pokesStandard,
    pokesLight,
    pokesSashimi,
    cakes,
    cookies,
    pies,
    dessertRolls,
  ]);

  return null;
}
